import { useCallback, useEffect, useState } from "react";
import _ from "lodash";
import moment from "moment";
import { NOT_AVAILABLE, ROLES } from "@/config";
import { Icon } from "@/components";
import {
    Badge,
    Button,
    Checkbox,
    DateRangePicker,
    FormLayout,
    Heading,
    IconButton,
    Input,
    LoadingProgress,
    Modal,
    Select,
    Text,
} from "@/ui";
import { currencyFormat } from "utils/utils";
import OrderHistory from "../components/OrderHistory";
import styles from "./GeneralPane.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const GeneralPane = ({
    datepickerChange,
    inputs,
    inputChange,
    loading,
    loadingReset,
    loadingUpdate,
    resetPassword,
    selectChange,
    updateTreezIds,
    validator,
}) => {
    const verified = inputs?.email_verified || false;
    const [treezIds, setTreezIds] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [tempValue, setTempValue] = useState("");
    const [actionDeleteActive, setActionDeleteActive] = useState(false);
    const [orderTotalCount, setOrderTotalCount] = useState(null);
    const [showChangeConfirm, setShowChangeConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const onListStoreOrdersData = useCallback(({ totalCount }) => {
        setOrderTotalCount(totalCount);
    }, []);

    const dateofbirth = moment(inputs?.date_of_birth || "")
        .utc()
        .format("MM/DD/YYYY");

    const dobDate =
        inputs?.date_of_birth && dateofbirth !== "Invalid date"
            ? moment(inputs.date_of_birth).utc().toDate()
            : undefined;

    const maxDobDate = moment().subtract(21, "year").endOf("day").toDate();

    const handleCancel = () => {
        setEditIndex(null);
        setTempValue("");
        setActionDeleteActive(false);
        setShowChangeConfirm(false);
        setShowDeleteConfirm(false);
    };

    useEffect(() => {
        setTreezIds(inputs?.treez_ids ?? []);
    }, [inputs?.treez_ids]);

    useEffect(() => {
        handleCancel();
    }, [loadingUpdate]);

    useEffect(() => {
        setOrderTotalCount(null);
    }, [inputs?.email]);

    const confirmChangePosId = () => {
        const updatedTreezIds = _.cloneDeep(treezIds);
        updatedTreezIds[editIndex].id = tempValue;
        setTreezIds(updatedTreezIds);
        handleCancel();
        updateTreezIds(updatedTreezIds);
    };

    const confirmDeletePosId = () => {
        const newTreezIds = treezIds?.filter((_, index) => index !== editIndex);
        setTreezIds(newTreezIds);
        handleCancel();
        updateTreezIds(newTreezIds);
    };

    const getName = () => {
        let name = null;

        if (inputs?.first_name) {
            name = `${inputs?.first_name}`;
        }

        if (inputs?.last_name) {
            name = `${name} ${inputs?.last_name}`;
        }

        if (name) {
            return name;
        }

        return NOT_AVAILABLE;
    };

    const handleEditCustomerID = (index, currentId) => {
        setEditIndex(index);
        setTempValue(currentId);
    };

    const handleDeleteCustomerID = index => {
        setEditIndex(index);
        setActionDeleteActive(true);
        setShowDeleteConfirm(true);
    };

    const roleOptions = ROLES.map(option => ({
        value: String(option.value),
        label: option.label,
    }));

    return (
        <div className={styles.root}>
            {loading ? (
                <div className={styles.loading}>
                    <LoadingProgress
                        indeterminate
                        indicator="circular"
                        size="sm"
                        title="Loading"
                    />
                </div>
            ) : (
                <>
                    {!inputs?.has_account ? (
                        <div className={styles.banner}>
                            <Badge
                                iconLeft={<Icon icon="info" />}
                                variant="neutral"
                            >
                                This user does not have login credentials
                            </Badge>
                        </div>
                    ) : null}

                    <Text className={styles.customerTitle} variant="body-lg">
                        {`${getName()} (Created at ${moment(
                            inputs?.created_at,
                        ).format("MMMM Do YYYY - HH:mm:ss")})`}
                    </Text>

                    <FormLayout>
                        <FormLayout.Section title="Details">
                            <FormLayout.Grid columns={4}>
                                <Input
                                    defaultValue={inputs.first_name ?? ""}
                                    error={fieldError(validator, "first_name")}
                                    fullWidth
                                    label="First Name"
                                    name="first_name"
                                    onChange={inputChange}
                                />
                                <Input
                                    defaultValue={inputs.last_name ?? ""}
                                    error={fieldError(validator, "last_name")}
                                    fullWidth
                                    label="Last Name"
                                    name="last_name"
                                    onChange={inputChange}
                                />
                                <Input
                                    defaultValue={inputs.phone ?? ""}
                                    error={fieldError(validator, "phone")}
                                    fullWidth
                                    label="Phone Number"
                                    name="phone"
                                    onChange={inputChange}
                                    type="tel"
                                />
                                <Input
                                    defaultValue={inputs.email ?? ""}
                                    disabled
                                    fullWidth
                                    iconRight={
                                        inputs?.email ? (
                                            <Badge
                                                className={
                                                    verified
                                                        ? styles.emailBadge
                                                        : `${styles.emailBadge} ${styles.emailBadgeUnverified}`
                                                }
                                                variant={
                                                    verified
                                                        ? "secondary"
                                                        : "neutral"
                                                }
                                            >
                                                {verified
                                                    ? "Verified"
                                                    : "Not Verified"}
                                            </Badge>
                                        ) : null
                                    }
                                    label="Email"
                                    name="email"
                                    onChange={inputChange}
                                    required
                                    type="email"
                                />
                                <DateRangePicker
                                    error={fieldError(
                                        validator,
                                        "date_of_birth",
                                    )}
                                    fullWidth
                                    label="Date of Birth"
                                    numberOfMonths={1}
                                    onChange={range => {
                                        if (!range?.from) return;
                                        datepickerChange(
                                            {
                                                singleDatePicker: true,
                                                startDate: moment(range.from),
                                                endDate: moment(range.from),
                                            },
                                            "date_of_birth",
                                        );
                                    }}
                                    showFooterActions={false}
                                    toDate={maxDobDate}
                                    value={dobDate ? { from: dobDate } : null}
                                />
                                <Select
                                    error={fieldError(validator, "role")}
                                    fullWidth
                                    label="Role"
                                    onValueChange={value =>
                                        selectChange({ value }, "role")
                                    }
                                    options={roleOptions}
                                    value={
                                        inputs?.role != null &&
                                        inputs?.role !== ""
                                            ? String(inputs.role)
                                            : undefined
                                    }
                                />
                                {inputs?.enabled ? (
                                    <div className={styles.resetPassword}>
                                        <Button
                                            disabled={loadingReset}
                                            fullWidth
                                            loading={loadingReset}
                                            onClick={resetPassword}
                                            size="lg"
                                            type="button"
                                            variant="primary"
                                        >
                                            Reset password
                                        </Button>
                                    </div>
                                ) : null}
                            </FormLayout.Grid>
                        </FormLayout.Section>

                        <FormLayout.Section title="Account Information">
                            <div className={styles.infoPanel}>
                                <div className={styles.infoColumn}>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            ID
                                        </Text>
                                        <Text className={styles.infoValue}>
                                            {inputs.entity_id || NOT_AVAILABLE}
                                        </Text>
                                    </div>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            Enrolment origin
                                        </Text>
                                        <div className={styles.originRow}>
                                            <span>TREEZ</span>
                                            <Badge variant="primary">
                                                Website
                                            </Badge>
                                            <span>Mode</span>
                                            <Badge variant="primary">
                                                Online
                                            </Badge>
                                        </div>
                                    </div>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            Marketing
                                        </Text>
                                        <Checkbox
                                            checked={
                                                inputs?.accept_marketing ||
                                                false
                                            }
                                            disabled
                                            label="Marketing"
                                        />
                                    </div>
                                </div>

                                <div className={styles.infoColumn}>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            IDP ID
                                        </Text>
                                        <Text className={styles.infoValue}>
                                            {inputs.id || NOT_AVAILABLE}
                                        </Text>
                                    </div>
                                    <div className={styles.infoField}>
                                        <div className={styles.treezHeader}>
                                            <Text
                                                className={styles.fieldLabel}
                                                variant="label-sm"
                                            >
                                                Accounts | Treez
                                            </Text>
                                        </div>
                                        <ul className={styles.treezList}>
                                            {treezIds?.map((a, index) => (
                                                <li
                                                    className={styles.treezItem}
                                                    key={index}
                                                >
                                                    <Badge variant="primary">
                                                        {a.store_id}
                                                    </Badge>
                                                    <div
                                                        className={
                                                            styles.treezEdit
                                                        }
                                                    >
                                                        {editIndex === index &&
                                                        !actionDeleteActive ? (
                                                            <>
                                                                <Input
                                                                    aria-label="Customer ID"
                                                                    className={
                                                                        styles.customerIdInput
                                                                    }
                                                                    density="compact"
                                                                    error={
                                                                        tempValue ===
                                                                        ""
                                                                    }
                                                                    label="Customer ID"
                                                                    labelBehavior="inline"
                                                                    onChange={e =>
                                                                        setTempValue(
                                                                            e
                                                                                .target
                                                                                .value,
                                                                        )
                                                                    }
                                                                    placeholder=""
                                                                    type="number"
                                                                    value={
                                                                        tempValue
                                                                    }
                                                                />
                                                                <IconButton
                                                                    aria-label="Save Customer Id"
                                                                    disabled={
                                                                        tempValue ===
                                                                        ""
                                                                    }
                                                                    onClick={() =>
                                                                        setShowChangeConfirm(
                                                                            true,
                                                                        )
                                                                    }
                                                                    size="sm"
                                                                    variant="tertiary"
                                                                >
                                                                    <Icon icon="save" />
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="Cancel Edit"
                                                                    onClick={
                                                                        handleCancel
                                                                    }
                                                                    size="sm"
                                                                    variant="tertiary"
                                                                >
                                                                    <Icon icon="cancel" />
                                                                </IconButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <span>
                                                                    {a.id}
                                                                </span>
                                                                <IconButton
                                                                    aria-label="Change Customer Id"
                                                                    onClick={() =>
                                                                        handleEditCustomerID(
                                                                            index,
                                                                            a.id,
                                                                        )
                                                                    }
                                                                    size="sm"
                                                                    variant="tertiary"
                                                                >
                                                                    <Icon icon="pencil" />
                                                                </IconButton>
                                                                <IconButton
                                                                    aria-label="Delete Record"
                                                                    onClick={() =>
                                                                        handleDeleteCustomerID(
                                                                            index,
                                                                        )
                                                                    }
                                                                    size="sm"
                                                                    variant="tertiary"
                                                                >
                                                                    <Icon icon="delete" />
                                                                </IconButton>
                                                            </>
                                                        )}
                                                    </div>
                                                </li>
                                            ))}
                                            {treezIds?.length === 0 ? (
                                                <li
                                                    className={styles.treezItem}
                                                    key="placeholder"
                                                >
                                                    <Text
                                                        className={
                                                            styles.treezPlaceholder
                                                        }
                                                        tone="secondary"
                                                        variant="body-sm"
                                                    >
                                                        No Customer POS ID is
                                                        associated
                                                    </Text>
                                                </li>
                                            ) : null}
                                        </ul>
                                    </div>
                                </div>

                                <div className={styles.infoColumn}>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            Created at
                                        </Text>
                                        <Text className={styles.infoValue}>
                                            {moment(inputs?.created_at).format(
                                                "MMMM Do YYYY - HH:mm:ss",
                                            )}
                                        </Text>
                                    </div>
                                    <div className={styles.infoField}>
                                        <Text
                                            className={styles.fieldLabel}
                                            variant="label-sm"
                                        >
                                            Status
                                        </Text>
                                        <Text className={styles.infoValue}>
                                            {inputs?.has_account
                                                ? "Active"
                                                : "Deactive"}
                                        </Text>
                                    </div>
                                </div>
                            </div>
                        </FormLayout.Section>
                    </FormLayout>

                    <Stats
                        totalOrders={
                            orderTotalCount ?? inputs?.total_orders ?? 0
                        }
                        totalSpend={inputs?.total_order_expense}
                    />

                    {inputs?.email ? (
                        <OrderHistory
                            email={inputs?.email}
                            onListStoreOrdersData={onListStoreOrdersData}
                        />
                    ) : null}
                </>
            )}

            <Modal
                description="Are you sure you want to change the customer POS's ID?"
                footer={
                    <div className={styles.modalActions}>
                        <Button onClick={confirmChangePosId} type="button">
                            Confirm
                        </Button>
                        <Button
                            onClick={handleCancel}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) handleCancel();
                }}
                open={showChangeConfirm}
                size="sm"
                title="Change POS ID"
            >
                {null}
            </Modal>

            <Modal
                description="Are you sure you want to disconnect the customer's POS ID?"
                footer={
                    <div className={styles.modalActions}>
                        <Button onClick={confirmDeletePosId} type="button">
                            Confirm
                        </Button>
                        <Button
                            onClick={handleCancel}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) handleCancel();
                }}
                open={showDeleteConfirm}
                size="sm"
                title="Disconnect POS ID"
            >
                {null}
            </Modal>
        </div>
    );
};

export default GeneralPane;

const Stats = ({ totalOrders, totalSpend }) => {
    const totalHits = totalOrders || 0;

    return (
        <section className={styles.stats}>
            <Heading level={5}>Orders Information</Heading>
            <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                    <Text className={styles.fieldLabel} variant="label-sm">
                        Lifetime Spend
                    </Text>
                    <span className={styles.statValue}>
                        {currencyFormat(totalSpend)}
                    </span>
                </div>
                <div className={styles.statCard}>
                    <Text className={styles.fieldLabel} variant="label-sm">
                        Average Spend
                    </Text>
                    <span className={styles.statValue}>
                        {totalHits > 0
                            ? currencyFormat(totalSpend / totalHits)
                            : "$0"}
                    </span>
                </div>
                <div className={styles.statCard}>
                    <Text className={styles.fieldLabel} variant="label-sm">
                        Total Orders
                    </Text>
                    <span className={styles.statValue}>{totalHits}</span>
                </div>
            </div>
        </section>
    );
};
